// src/component/PriceProductModal.jsx
import React, { useEffect, useState } from "react";
import {
  FaHeart,
  FaRegHeart,
  FaShoppingCart,
  FaPlus,
  FaMinus,
  FaTimes,
} from "react-icons/fa";
import SafeImage from "./ui/SafeImage";

const fmt = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("ru-RU");

export default function PriceProductModal({
  open,
  onClose,
  product,
  cartQty = 0,
  onAddToCart,
  onDecreaseOne,
  isFavorite,
  onToggleFavorite,
}) {
  // Все хуки — ДО любого раннего return
  const [showFullDesc, setShowFullDesc] = useState(false);

  // ESC для закрытия
  useEffect(() => {
    if (!open) return;
    const key = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [open, onClose]);

  // Сбрасываем разворот описания при каждом открытии/смене товара
  useEffect(() => {
    if (open) setShowFullDesc(false);
  }, [open, product?.id]);

  // Ранний выход — ПОСЛЕ хуков
  if (!open || !product) return null;

  const getImgSrc = (p) => {
    const img = p?.image;
    if (!img) return null;
    return img.startsWith?.("http") ? img : `/media/${img}`;
  };

  const totalQty = Array.isArray(product.stores)
    ? product.stores.reduce((acc, s) => acc + (Number(s.quantity) || 0), 0)
    : 0;

  const minPrice = Array.isArray(product.stores)
    ? Math.min(
        ...product.stores
          .filter((s) => s.price != null)
          .map((s) => Number(s.price)),
        Infinity
      )
    : null;

  const out = totalQty <= 0;

  // Текст описания из возможных полей
  const description = (
    product?.description ??
    product?.desc ??
    product?.long_description ??
    product?.text ??
    ""
  )
    .toString()
    .trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3 sm:items-center"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-xl rounded-2xl bg-white shadow-2xl ring-1 ring-neutral-200"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Хедер карточки: заголовок + три кнопки (корзина, избранное, закрыть) */}
        <div className="flex items-start gap-2 rounded-t-2xl p-4">
          <h3 className="mr-auto text-lg font-semibold text-neutral-900">
            {product.name}
          </h3>

          <button
            className={`rounded-full p-2 shadow-sm ring-1 ring-neutral-200 ${
              out
                ? "text-neutral-400 bg-neutral-50"
                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            }`}
            onClick={() => !out && onAddToCart?.(1)}
            disabled={out}
            title={out ? "Нет в наличии" : "Добавить в корзину"}
          >
            <FaShoppingCart />
          </button>

          <button
            className="rounded-full bg-white p-2 text-red-500 shadow-sm ring-1 ring-neutral-200 hover:bg-red-50"
            onClick={onToggleFavorite}
            title={isFavorite ? "Убрать из избранного" : "В избранное"}
            aria-pressed={!!isFavorite}
          >
            {isFavorite ? <FaHeart /> : <FaRegHeart />}
          </button>

          <button
            className="rounded-full bg-white p-2 text-neutral-600 shadow-sm ring-1 ring-neutral-200 hover:bg-neutral-100"
            onClick={onClose}
            title="Закрыть"
            aria-label="Закрыть"
          >
            <FaTimes />
          </button>
        </div>

        {/* Контент */}
        <div className="px-4 pb-4">
          <div className="overflow-hidden rounded-xl bg-white">
            <div className="aspect-[4/3] w-full">
              <SafeImage
                src={getImgSrc(product)}
                alt={product.name}
                className="h-full w-full select-none object-contain p-4"
                fallback="/no-image.svg"
              />
            </div>
          </div>

          {/* Описание товара */}
          {description && (
            <div className="mt-4">
              <h4 className="mb-1 text-sm font-semibold text-neutral-800">
                Описание
              </h4>
              <div
                className={`relative text-sm text-neutral-700 break-words ${
                  showFullDesc ? "" : "max-h-40 overflow-hidden"
                }`}
              >
                <div className="whitespace-pre-line">{description}</div>
                {!showFullDesc && description.length > 280 && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white"></div>
                )}
              </div>
              {description.length > 280 && (
                <button
                  type="button"
                  className="mt-1 text-sm font-medium text-emerald-700 hover:underline"
                  onClick={() => setShowFullDesc((v) => !v)}
                >
                  {showFullDesc ? "Свернуть" : "Читать полностью"}
                </button>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700">
              {minPrice !== null && minPrice !== Infinity
                ? `${fmt(minPrice)} ₽`
                : "—"}
            </div>
            <div
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs ${
                out
                  ? "bg-red-50 text-red-600 ring-1 ring-red-100"
                  : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
              }`}
            >
              {out ? "Нет в наличии" : `В наличии всего: ${fmt(totalQty)} шт.`}
            </div>
          </div>

          <div className="mt-3 space-y-1 text-sm text-neutral-700">
            {product.sku && (
              <div>
                <span className="text-neutral-500">Артикул: </span>
                <span className="break-all">{product.sku}</span>
              </div>
            )}
            {product.warranty && (
              <div>
                <span className="text-neutral-500">Гарантия: </span>
                <span>{product.warranty}</span>
              </div>
            )}
          </div>

          {Array.isArray(product.stores) && product.stores.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-neutral-800">
                Остатки и цены по магазинам
              </h4>
              <ul className="space-y-1 text-sm text-neutral-700">
                {product.stores.map((s) => (
                  <li key={s.store} className="flex justify-between">
                    <span className="truncate pr-2">{s.store_name}</span>
                    <span className="whitespace-nowrap">
                      {fmt(s.quantity)} шт
                      {typeof s.price === "number" ? ` — ${fmt(s.price)} ₽` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Низ карточки */}
        <div className="flex items-center justify-between gap-3 rounded-b-2xl border-t bg-neutral-50 px-4 py-3">
          {cartQty > 0 ? (
            <div className="flex items-center gap-2">
              <button
                className="rounded-full bg-emerald-700 p-2 text-white hover:bg-emerald-800"
                onClick={onDecreaseOne}
                aria-label="–"
              >
                <FaMinus size={12} />
              </button>
              <span className="min-w-[20px] text-center text-sm">{cartQty}</span>
              <button
                className="rounded-full bg-emerald-700 p-2 text-white hover:bg-emerald-800"
                onClick={() => onAddToCart?.(1)}
                aria-label="+"
              >
                <FaPlus size={12} />
              </button>
            </div>
          ) : (
            <div className="text-sm text-neutral-600">
              Добавьте в корзину, чтобы выбрать количество
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              className={`rounded-lg px-4 py-2 text-sm font-semibold shadow-sm ${
                out
                  ? "cursor-not-allowed bg-neutral-200 text-neutral-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
              onClick={() => !out && onAddToCart?.(1)}
              disabled={out}
            >
              {out ? "Нет в наличии" : "В корзину"}
            </button>
            <button
              className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
              onClick={onClose}
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
